import { useEffect, useState } from 'react';
import { preloadImage } from '../../helpers/image';

/** URLs we have already started or finished preloading; avoids duplicate Image() requests for the same URL (e.g. when hovering back to the same segment). */
const preloadedUrls = new Set<string>();

/**
 * Returns true once the image at `url` has been loaded and decoded (or was already preloaded).
 * Uses Image.decode() so the thumbnail is only shown when ready to paint without jank.
 * Each URL is only preloaded once per session so the browser can reuse its cache.
 */
export function useImagePreload(url?: string): boolean {
  const [loaded, setLoaded] = useState(() => !!url && preloadedUrls.has(url));

  useEffect(() => {
    if (!url) {
      return;
    }
    if (preloadedUrls.has(url)) {
      setLoaded(true);
      return;
    }

    setLoaded(false);
    preloadImage(url, () => {
      preloadedUrls.add(url);
      setLoaded(true);
    });
  }, [url]);

  return loaded;
}
